import React, { useMemo, useState } from "react";
import "./pricing.css";
import { useNavigate } from 'react-router-dom';

const DEFAULT_PLANS = [
  {
    id: "student",
    tag: "For 1 Student",
    title: "Student",
    priceMonthly: 999,
    priceYearly: 10500,
    desc: "Explore core features with basic limits.",
    bullets: [
      "Correct Solution",
      "Explanation of mistakes to avoid.",
      "Grammatical Error feedback.",
      "310 questions to practice in a month.",
      "Daily Whatsapp Summary of all the test."
    ],
    accent: "mint",
    featured: true,
  },
  {
    id: "student+",
    tag: "For Power Users",
    title: "Student+",
    priceMonthly: 1799,
    priceYearly: 19000,
    desc: "More Practice for future champion.",
    bullets: [
      "Correct Solution",
      "Explanation of mistakes to avoid.",
      "Grammatical Error feedback.",
      "620 questions to practice in a month.",
      "Daily Whatsapp Summary of all the test."
    ],
    accent: "blue"
  },
  {
    id: "pro",
    tag: "Family Pack",
    title: "Family",
    priceMonthly: 2999,
    priceYearly: 31500,
    desc: "One plan for all.",
    bullets: [
      "Correct Solution.",
      "Explanation of mistakes to avoid.",
      "Grammatical Error feedback.",
      "1250 questions to practice in a month.",
      "Daily Whatsapp Summary of all the test."
    ],
    accent: "violet",
  },
  {
    id: "enterprise",
    tag: "Custom",
    title: "Enterprise",
    priceMonthly: 0,
    priceYearly: 0,
    desc: "Custom integrations, dedicated support.",
    bullets: [
      "All Core Features.",
      "Custom Solution for your Problems.",
      "Access to Pre Released Features."
    ],
    accent: "slate",
    ctaLabel: "Contact sales",
  },
];

function formatINR(n) {
  if (!n) return "₹0";
  try {
    return new Intl.NumberFormat("en-IN").format(n).replace(/^/, "₹");
  } catch {
    // fallback
    return `₹${n}`;
  }
}

export default function PriceScreen({
  heading = "Pricing that scales with you",
  subheading = "Choose a plan. Upgrade anytime.",
  refund  = "*If you consume less than 10% of question quota of your plan and don’t like the experience, we’ll refund you in full.",
  plans = DEFAULT_PLANS
}) {
  const [billing, setBilling] = useState("monthly"); // "monthly" | "yearly"
  const navigate = useNavigate();


  const computedPlans = useMemo(() => {
    return plans.map((p) => {
      const price =
        billing === "monthly" ? p.priceMonthly : p.priceYearly;

      const isCustom =
        (billing === "monthly" && p.priceMonthly === 0 && p.ctaLabel === "Contact sales") ||
        (billing === "yearly" && p.priceYearly === 0 && p.ctaLabel === "Contact sales");

      return { ...p, displayPrice: isCustom ? "Custom" : formatINR(price) };
    });
  }, [plans, billing]);

  const onSelectPlan = () => {
    navigate("/about", { replace: true });
  };

  return (
    <section className="paper-grid pricing-wrap">
      <div className="pricing-inner">
        <div className="pricing-head">
          <div>
            <h2 className="pricing-title">{heading}</h2>
            <p className="pricing-sub">{subheading}</p>
            <p className="pricing-sub">{refund}</p>
          </div>

          <div className="billing-toggle shadow-soft" role="tablist" aria-label="Billing period">
            <button
              type="button"
              className={["toggle-btn", billing === "monthly" ? "active" : ""].join(" ")}
              onClick={() => setBilling("monthly")}
              role="tab"
              aria-selected={billing === "monthly"}
            >
              Monthly
            </button>
            <button
              type="button"
              className={["toggle-btn", billing === "yearly" ? "active" : ""].join(" ")}
              onClick={() => setBilling("yearly")}
              role="tab"
              aria-selected={billing === "yearly"}
            >
              Yearly <span className="chip-save">Save</span>
            </button>
          </div>
        </div>

        <div className="pricing-scroll" aria-label="Pricing plans">
          <div className="pricing-row">
            {computedPlans.map((plan) => (
              <article
                key={plan.id}
                className={[
                  "price-card",
                  "shadow-soft",
                  `accent-${plan.accent}`,
                  plan.featured ? "featured" : "",
                ].join(" ")}
              >
                <div className="card-top">
                  <div className="top-left">
                    <div className="tag">{plan.tag}</div>
                    <h3 className="plan-title">{plan.title}</h3>
                  </div>

                  <div className="spark" aria-hidden="true" />
                </div>

                <div className="price-line">
                  <div className="price">
                    {plan.displayPrice}
                    {plan.displayPrice !== "Custom" && plan.displayPrice !== "₹0" ? (
                      <span className="per">/{billing === "monthly" ? "mo" : "yr"}</span>
                    ) : plan.displayPrice === "₹0" ? (
                      <span className="per">Forever</span>
                    ) : null}
                  </div>
                  <p className="desc">{plan.desc}</p>
                </div>

                <ul className="bullets">
                  {plan.bullets.map((b, idx) => (
                    <li key={idx} className="bullet">
                      <span className="check" aria-hidden="true">✓</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  className={["cta", plan.featured ? "cta-primary" : "cta-ghost"].join(" ")}
                  onClick={() => onSelectPlan(plan.id)}
                >
                  {plan.ctaLabel ||
                    (plan.displayPrice === "₹0" ? "Get started" : "Contact Us")}
                </button>

                {plan.featured ? (
                  <div className="featured-note">
                    Recommended for most learners
                  </div>
                ) : null}
              </article>
            ))}
          </div>
        </div>

        <div className="pricing-foot">
          <div className="mini-note">
            Prices in INR (₹). Taxes may apply. Cancel anytime.
          </div>
          <div className="mini-note">
            *If you consume less than 10% of question quota your plan and don’t like the experience, we’ll refund you in full.
          </div>
        </div>
      </div>
    </section>
  );
}
